import { DestroyRef, Injectable, Injector, WritableSignal, effect, inject, signal } from '@angular/core';
import {
  HD_BODYGRAPH_COLOR_CONFIG_PRESETS,
  HD_BODYGRAPH_DEFAULT_COLOR_CONFIG,
  HdBodygraphActiveGateColorPreset,
  HdBodygraphCenterBorderStyle,
  HdBodygraphColorConfig,
  HdBodygraphColorConfigPresetId,
} from '@/app/human-design/chart/bodygraph/hd-bodygraph-color-config';
import {
  HD_PLANET_GRID_COLOR_CONFIG_PRESETS,
  HD_PLANET_GRID_DEFAULT_COLOR_CONFIG,
  HdBaseRotation,
  HdBeneathTheLineStyle,
  HdGateNumberAlign,
  HdGateNumberFontSize,
  HdNodeBackgroundStyle,
  HdPlanetGridColorConfig,
  HdPlanetGridColorConfigPresetId,
  HdUndefinedActivationStyle,
} from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-grid-color-config';
import { HdGatePrimaryAction } from '@/app/human-design/chart/bodygraph/hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HdGateInfoOpenBehavior } from '@/app/human-design/chart/bodygraph/gate-info/hd-gate-info-requested-context.model';
import { HdBridgeGateStyle } from '@/app/human-design/chart/bodygraph/hd-chart-controls-options';
import { HdPlanetGridVisualizationMode } from '@/app/human-design/chart/bodygraph/hd-bodygraph.models';
import { HdChartButtonSize } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-shortcut-bar.component';
import {
  HdMoonTransitConnectorStyle,
  HdMoonTransitGateMarkerStyle,
} from '@/app/human-design/chart/mandala/moon-transit/hd-moon-transit-view.models';
import { HdDocLinkOpenBehavior } from '@/app/human-design/chart/bodygraph/doc-panel/hd-doc-link-open-behavior';
import { HD_GATE_GRAPH_PALETTE_DEFAULT, HdGateGraphPaletteId } from '@/app/human-design/chart/bodygraph/gate-graph/hd-gate-graph-palette';
import { HD_GATE_GRAPH_LAYOUT } from '@/app/human-design/chart/bodygraph/gate-graph/hd-gate-graph-layout';
import { HdJustNowTransitColorMode } from '@/app/human-design/chart/mandala/just-now-transit/hd-just-now-transit';
import {
  HdMandalaQuarterColorVariant,
  HdMandalaZodiacRingVariant,
} from '@/app/human-design/chart/mandala/mandala.constants';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HdActivePaletteService } from '@/app/human-design/chart/bodygraph/hd-active-palette.service';
import { HdLocksKeysStateService } from '@/app/human-design/chart/mandala/locks-keys/hd-locks-keys-state.service';
import { HdChartGateSearchSort } from '@/app/human-design/chart/library/hd-chart-gate-match-sort';
import {
  HD_HOVER_HIGHLIGHT_COLOR_DEFAULT,
  HdHoverHighlightColor,
  coerceHdHoverHighlightColor,
} from '@/app/human-design/chart/bodygraph/hd-hover-highlight-theme';
import { PHONE_MEDIA_QUERY } from '@common/viewport/viewport-breakpoints';
import { IChingCastDistribution, IChingCastMode } from '@/app/human-design/iching/core/iching.models';
import {
  HdChartGlobalSettingsV1,
  HdChartMatchScope,
  HdChartSettingsProfileId,
  activeHdChartSettingsProfile,
  createDefaultHdChartGlobalSettings,
  loadHdChartGlobalSettings,
  pickExternallyManagedSettings,
  saveHdChartGlobalSettings,
} from './hd-chart-global-settings';

/** Debounce van de settings-persistentie (was GLOBAL_SETTINGS_PERSIST_DEBOUNCE_MS in de chart-pagina). */
const PERSIST_DEBOUNCE_MS = 300;

/**
 * Centrale, tab-overstijgende opslagplaats van álle HD-app-settings (kleuren,
 * planeetgrid, interactiegedrag, mandala-varianten, transit-voorkeuren, …).
 *
 * Bewust een **custom signal-service** (geen Akita): de settings waren al platte
 * signals + localStorage, en dit is de "één centrale plek" waar elke consument ze
 * ophaalt en waar wijzigingen vandaan propageren. De chart-omgeving (en zijn
 * basisklasse) aliassen hun bestaande signals op deze store, zodat álle lees- en
 * schrijfplekken ongewijzigd blijven en er één bron van waarheid is. Het
 * settings-paneel (app-breed gehost) leest/schrijft via dezelfde signals en
 * handlers.
 *
 * Enkele velden hebben een eigen autoriteit-service en worden gedelegeerd:
 * `contentLangOverride` ({@link HdContentLanguageService}) en `locksKeysPeriod`
 * + `locksKeysChartOverlay` + `locksKeysSearchSort`/`locksKeysSearchFilter`
 * ({@link HdLocksKeysStateService}). De
 * transit-view-velden worden door de transit-componenten beheerd en enkel
 * meegepersisteerd.
 */
@Injectable({ providedIn: 'root' })
export class HdChartSettingsStore {
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly injector = inject(Injector);
  private readonly locksKeys = inject(HdLocksKeysStateService);

  // ── Kleuren / planeetgrid ────────────────────────────────────────────────
  readonly colorConfig: WritableSignal<HdBodygraphColorConfig> = signal(HD_BODYGRAPH_DEFAULT_COLOR_CONFIG);
  readonly colorConfigPresetId: WritableSignal<HdBodygraphColorConfigPresetId> = signal('warm-dusk');
  readonly planetGridColorConfig: WritableSignal<HdPlanetGridColorConfig> = signal(HD_PLANET_GRID_DEFAULT_COLOR_CONFIG);
  readonly planetGridColorConfigPresetId: WritableSignal<HdPlanetGridColorConfigPresetId> = signal('warm-dusk');
  readonly showPlanetGrid = signal(true);
  readonly simplePlanetGrid = signal(false);
  readonly advancedLpsGrid = signal(false);
  readonly miniPlanetGrid = signal(false);
  readonly showChiron = signal(true);
  readonly showAstroPositions = signal(false);
  readonly showGateBlocks = signal(false);
  readonly planetGridVisualizationMode: WritableSignal<HdPlanetGridVisualizationMode> = signal('extended');

  // ── Inhoud / keynotes ────────────────────────────────────────────────────
  readonly includeNonHdFeatures = signal(false);
  readonly showGeneKeys = signal(false);
  readonly showHdKeynotes = signal(false);
  readonly keynotesAtHoverOnly = signal(true);
  readonly showBothCenterStateSections = signal(true);
  readonly showCenterCheatSheet = signal(false);

  // ── Interactiegedrag ─────────────────────────────────────────────────────
  readonly chartGatePrimaryAction: WritableSignal<HdGatePrimaryAction> = signal('show-info');
  readonly chartContextMenuEnabled = signal(true);
  readonly channelHighlightEnabled = signal(true);
  /** Dimmen bij hover; `false` = enkel blauwe-rand-highlights, niets dimt. */
  readonly dimOnHover = signal(true);
  /** Kleur van de hover-highlightranden (zie `hd-hover-highlight-theme.ts`). */
  readonly hoverHighlightColor: WritableSignal<HdHoverHighlightColor> = signal(HD_HOVER_HIGHLIGHT_COLOR_DEFAULT);
  /** Outline rond het kanaalstuk van gehighlighte poorten; default aan. */
  readonly highlightGatePieces = signal(true);
  /** Basis-paneel (center- en activatie-kaartjes) gekoppeld aan de chart-hover; zwaar, default uit. */
  readonly basicPanelChartLink = signal(false);
  /** Enkel de kant tegenover de hover-bron dimmen (chart ↔ planeetgrid); default aan. */
  readonly dimOneSideOnly = signal(true);
  /** Community-content (Line Companion e.d.) tonen; opt-in, default uit. */
  readonly communityContentEnabled = signal(false);
  /**
   * Alternatieve I Tjing-interpretaties (The Book of Lines, Chetan Parkyn) als
   * extra regel bij de exaltation/detriment van elke lijn; opt-in, default uit.
   */
  readonly alternativeIChingEnabled = signal(false);
  /** Ra-lijnteksten (blauwe lijnbeschrijving + White/Black Book-keynotes); default aan. */
  readonly raLineTextsEnabled = signal(true);
  /** Eigen lijnteksten (Mechanics in Motion); opt-in, default uit. */
  readonly ownLineTextsEnabled = signal(false);
  /**
   * Classic I Ching (Wilhelm NL / Legge EN) bij de poorten en als pagina; opt-in,
   * default uit. Zichtbaarheid vereist daarnaast de UPAM-feature (zie `HdIChingAccessService`).
   */
  readonly classicIChingEnabled = signal(false);
  /** Laatst gekozen worpmodus op de Classic I Ching-pagina. */
  readonly ichingCastMode: WritableSignal<IChingCastMode> = signal('coins');
  /** Kansverdeling van de virtuele worp (munten of duizendblad). */
  readonly ichingCastDistribution: WritableSignal<IChingCastDistribution> = signal('coins');
  /** Introductie op de Classic I Ching-pagina ingeklapt. */
  readonly ichingIntroCollapsed = signal(false);
  /** Classic I Ching-doorklikiconen bij de chart (Geavanceerd, composite). */
  readonly classicIChingIconsChart = signal(true);
  /** Classic I Ching-doorklikiconen bij transits (kalender, Just Now, ephemeris). */
  readonly classicIChingIconsTransits = signal(true);
  readonly gateInfoOpenBehavior: WritableSignal<HdGateInfoOpenBehavior> = signal('full-docked-keep');
  readonly gateGraphPalette: WritableSignal<HdGateGraphPaletteId> = signal(HD_GATE_GRAPH_PALETTE_DEFAULT);
  readonly gateGraphSpacing: WritableSignal<number> = signal(HD_GATE_GRAPH_LAYOUT.spacing.default);
  readonly docLinkOpenBehavior: WritableSignal<HdDocLinkOpenBehavior> = signal('same-panel');
  readonly gateInfoFromChannelSeparateToDock = signal(true);
  readonly advancedBridgeCalculation = signal(true);
  readonly bridgeGateStyle: WritableSignal<HdBridgeGateStyle> = signal('badge');

  // ── Transit / mandala ────────────────────────────────────────────────────
  readonly moonTransitConnectorStyle: WritableSignal<HdMoonTransitConnectorStyle> = signal('curved');
  readonly moonTransitGateMarkerStyle: WritableSignal<HdMoonTransitGateMarkerStyle> = signal('ring');
  readonly justNowTransitColorMode: WritableSignal<HdJustNowTransitColorMode> = signal('three-color');
  readonly mandalaQuarterColorVariant: WritableSignal<HdMandalaQuarterColorVariant> = signal('light');
  readonly mandalaZodiacRingVariant: WritableSignal<HdMandalaZodiacRingVariant> = signal('dark');
  readonly mandalaSliceCenterFade = signal(true);

  // ── Layout ───────────────────────────────────────────────────────────────
  readonly configPanelCollapsed = signal(false);
  /** Breedte van het rechterpaneel via de sleeplijn; `undefined` = standaard. */
  readonly configPanelWidth = signal<number | undefined>(undefined);
  readonly shortcutButtonSize: WritableSignal<HdChartButtonSize> = signal('sm');
  readonly chartMatchScope: WritableSignal<HdChartMatchScope> = signal('library');
  /** Poortzoektocht in de poort-info: zwaarste activaties eerst, of alfabetisch. */
  readonly gateSearchSort: WritableSignal<HdChartGateSearchSort> = signal('weight');

  /**
   * Open-toestand van het settings-paneel. Start bewust op `false` (net als de
   * chart-pagina vroeger): een sessie begint zonder open paneel.
   */
  readonly panelOpen = signal(false);

  /**
   * Print-actie van de actieve chart-weergave; `null` op HD-pagina's zonder
   * chart (de app-brede paneel-host verbergt de print-knop dan). De
   * chart-pagina registreert dit bij activatie en wist het bij vernietiging.
   */
  readonly printHandler = signal<(() => void) | null>(null);

  /** Voorkomt dat het persist-effect tijdens de eerste hydrate wegschrijft. */
  private hydrated = false;

  /**
   * Het profiel (desktop/mobiel) waaruit de signals gevuld zijn, en dus het
   * enige profiel waarnaar we wegschrijven. Bugfix 2026-09-26: het profiel werd
   * bij elke write opnieuw uit de viewport afgeleid. Een telefoon die in
   * portrait laadde en in landscape (≥ 600px = desktop-profiel) naar de
   * achtergrond ging, schreef zo zijn mobiele waarden (mini-grid, keynotes niet
   * enkel bij hover, ...) in het desktop-profiel, en de account-sync bracht die
   * naar elke desktop.
   */
  private profile: HdChartSettingsProfileId = 'desktop';

  constructor() {
    this.hydrate();
    // Center-iconen en -badges buiten de bodygraph volgen zo live het palet.
    inject(HdActivePaletteService).registerColorConfig(this.colorConfig);

    // De eigen lijnteksten zitten in een eigen scope die pas laadt wanneer de
    // setting aan staat: wie ze niet gebruikt, betaalt ook niet voor de extra
    // vertaalsleutels (elke scope-scan loopt over de volledige taaltabel). De
    // gate-info-service pas hier ophalen houdt hem uit de store-constructie.
    effect(() => {
      if (!this.ownLineTextsEnabled()) {
        return;
      }
      const lang = this.contentLang.effectiveLang();
      this.injector.get(HdGateInfoService).loadOwnLineTextsScope(lang);
    });

    effect(() => {
      const settings = this.collect();
      if (this.hydrated) {
        this.schedulePersist(settings);
      }
    });

    // Laatste debounced write veiligstellen bij het verbergen van het tabblad.
    const onHidden = (): void => {
      if (document.visibilityState === 'hidden') this.flush();
    };
    document.addEventListener('visibilitychange', onHidden);

    // Kruist de viewport de phone-grens (telefoon draaien, venster versmallen),
    // dan hoort er een ander profiel bij.
    const phoneQuery = window.matchMedia(PHONE_MEDIA_QUERY);
    const onDeviceClassChange = (): void => this.switchProfile(activeHdChartSettingsProfile());
    phoneQuery.addEventListener('change', onDeviceClassChange);

    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('visibilitychange', onHidden);
      phoneQuery.removeEventListener('change', onDeviceClassChange);
      if (this.persistTimer != null) {
        clearTimeout(this.persistTimer);
        saveHdChartGlobalSettings(this.collect(), this.profile);
      }
    });
  }

  private persistTimer: ReturnType<typeof setTimeout> | null = null;

  private hydrate(): void {
    this.profile = activeHdChartSettingsProfile();
    this.applySettings(loadHdChartGlobalSettings(this.profile));
    this.hydrated = true;
  }

  /**
   * Bewaart eerst wat nog openstaat in het huidige profiel en laadt daarna het
   * profiel van de nieuwe deviceklasse, zodat de weergave dezelfde is als bij
   * een verse load op die breedte.
   */
  private switchProfile(next: HdChartSettingsProfileId): void {
    if (next === this.profile) {
      return;
    }
    this.flush();
    this.profile = next;
    this.applySettings(loadHdChartGlobalSettings(next));
  }

  /**
   * Herstelt álle settings naar de **app-defaults** (fabrieksinstellingen), niet
   * de eigen opgeslagen settings. De persist-effect schrijft de defaults daarna
   * weg zodat ze behouden blijven. `panelOpen` blijft onaangeroerd zodat het
   * paneel open blijft en de gebruiker de reset meteen ziet.
   */
  resetToDefaults(): void {
    // BELANGRIJK — de Info/Edit-stand (`chartGatePrimaryAction`) is een modus van
    // de chart, geen voorkeur: hij blijft bij een reset staan. Toen de reset hem
    // nog op de default zette, viel de hover-highlight stil (die werkt enkel in
    // `show-info`) terwijl alle hover-settings er goed uitzagen; pas het openen
    // van een nieuwe chart zette hem terug (Steven, prod 2026-10-13). Zie de spec
    // "laat de Info/Edit-stand bij een reset staan".
    this.applySettings({
      ...createDefaultHdChartGlobalSettings(),
      chartGatePrimaryAction: this.chartGatePrimaryAction(),
    });
  }

  /** Zet alle signals vanuit een settings-blob (hydrate én reset delen dit). */
  private applySettings(s: HdChartGlobalSettingsV1): void {
    this.colorConfig.set(s.colorConfig);
    this.colorConfigPresetId.set(s.colorConfigPresetId);
    // Uitlijning poortnummers is niet langer instelbaar: altijd rechts (de
    // controle is uit het paneel verwijderd). Coerce een eventueel oude,
    // opgeslagen 'left' zodat bestaande gebruikers ook rechts krijgen.
    this.planetGridColorConfig.set({ ...s.planetGridColorConfig, gateNumberAlign: 'right' });
    this.planetGridColorConfigPresetId.set(s.planetGridColorConfigPresetId);
    this.showPlanetGrid.set(s.showPlanetGrid);
    this.simplePlanetGrid.set(s.simplePlanetGrid);
    this.advancedLpsGrid.set(s.advancedLpsGrid);
    this.miniPlanetGrid.set(s.miniPlanetGrid);
    this.showChiron.set(s.showChiron);
    this.showAstroPositions.set(s.showAstroPositions);
    this.showGateBlocks.set(s.showGateBlocks);
    this.planetGridVisualizationMode.set(s.planetGridVisualizationMode);
    this.includeNonHdFeatures.set(s.includeNonHdFeatures);
    // showGeneKeys enkel als niet-HD-features aan staan (zelfde regel als voorheen).
    this.showGeneKeys.set(s.includeNonHdFeatures && s.showGeneKeys);
    this.showHdKeynotes.set(s.showHdKeynotes);
    this.keynotesAtHoverOnly.set(s.keynotesAtHoverOnly);
    this.showBothCenterStateSections.set(s.showBothCenterStateSections);
    this.showCenterCheatSheet.set(s.showCenterCheatSheet);
    this.chartGatePrimaryAction.set(s.chartGatePrimaryAction);
    this.chartContextMenuEnabled.set(s.chartContextMenuEnabled);
    this.channelHighlightEnabled.set(s.channelHighlightEnabled);
    this.dimOnHover.set(s.dimOnHover);
    this.hoverHighlightColor.set(coerceHdHoverHighlightColor(s.hoverHighlightColor));
    this.highlightGatePieces.set(s.highlightGatePieces);
    this.dimOneSideOnly.set(s.dimOneSideOnly);
    this.basicPanelChartLink.set(s.basicPanelChartLink);
    this.communityContentEnabled.set(s.communityContentEnabled);
    this.alternativeIChingEnabled.set(s.alternativeIChingEnabled);
    this.raLineTextsEnabled.set(s.raLineTextsEnabled);
    this.ownLineTextsEnabled.set(s.ownLineTextsEnabled);
    this.classicIChingEnabled.set(s.classicIChingEnabled);
    this.ichingCastMode.set(s.ichingCastMode);
    this.ichingCastDistribution.set(s.ichingCastDistribution);
    this.ichingIntroCollapsed.set(s.ichingIntroCollapsed);
    this.classicIChingIconsChart.set(s.classicIChingIconsChart);
    this.classicIChingIconsTransits.set(s.classicIChingIconsTransits);
    // Open-gedrag info-panelen kent nog enkel de twee gedockte varianten; de
    // vroegere (undockte) 'small'/'full' worden gecoërceerd naar "behoud".
    this.gateInfoOpenBehavior.set(
      s.gateInfoOpenBehavior === 'small' || s.gateInfoOpenBehavior === 'full'
        ? 'full-docked-keep'
        : s.gateInfoOpenBehavior,
    );
    this.gateGraphPalette.set(s.gateGraphPalette);
    this.gateGraphSpacing.set(s.gateGraphSpacing);
    this.docLinkOpenBehavior.set(s.docLinkOpenBehavior);
    this.gateInfoFromChannelSeparateToDock.set(s.gateInfoFromChannelSeparateToDock);
    this.advancedBridgeCalculation.set(s.advancedBridgeCalculation);
    this.bridgeGateStyle.set(s.bridgeGateStyle);
    this.moonTransitConnectorStyle.set(s.moonTransitConnectorStyle);
    this.moonTransitGateMarkerStyle.set(s.moonTransitGateMarkerStyle);
    this.justNowTransitColorMode.set(s.justNowTransitColorMode);
    this.mandalaQuarterColorVariant.set(s.mandalaQuarterColorVariant);
    this.mandalaZodiacRingVariant.set(s.mandalaZodiacRingVariant);
    this.mandalaSliceCenterFade.set(s.mandalaSliceCenterFade);
    this.configPanelCollapsed.set(s.configPanelCollapsed);
    this.configPanelWidth.set(validConfigPanelWidth(s.configPanelWidth));
    this.shortcutButtonSize.set(s.shortcutButtonSize);
    this.chartMatchScope.set(s.chartMatchScope);
    this.gateSearchSort.set(s.gateSearchSort === 'name' ? 'name' : 'weight');
    // panelOpen bewust niet hydrateren.
    // Via restorePeriods: het periodepaar start op dat van nu, met de bewaarde
    // kant actief.
    this.locksKeys.restorePeriods(s.locksKeysPeriod);
    this.locksKeys.chartOverlayMode.set(s.locksKeysChartOverlay);
    this.locksKeys.restoreSearchPreferences(s.locksKeysSearchSort, s.locksKeysSearchFilter);
    this.contentLang.setOverride(s.contentLangOverride);
  }

  /** De volledige, platte settings — voor persistentie. */
  collect(): HdChartGlobalSettingsV1 {
    return {
      schemaVersion: 1,
      colorConfig: this.colorConfig(),
      colorConfigPresetId: this.colorConfigPresetId(),
      planetGridColorConfig: this.planetGridColorConfig(),
      planetGridColorConfigPresetId: this.planetGridColorConfigPresetId(),
      showPlanetGrid: this.showPlanetGrid(),
      simplePlanetGrid: this.simplePlanetGrid(),
      advancedLpsGrid: this.advancedLpsGrid(),
      miniPlanetGrid: this.miniPlanetGrid(),
      showChiron: this.showChiron(),
      showAstroPositions: this.showAstroPositions(),
      showGateBlocks: this.showGateBlocks(),
      planetGridVisualizationMode: this.planetGridVisualizationMode(),
      includeNonHdFeatures: this.includeNonHdFeatures(),
      showGeneKeys: this.showGeneKeys(),
      showHdKeynotes: this.showHdKeynotes(),
      showBothCenterStateSections: this.showBothCenterStateSections(),
      showCenterCheatSheet: this.showCenterCheatSheet(),
      keynotesAtHoverOnly: this.keynotesAtHoverOnly(),
      chartGatePrimaryAction: this.chartGatePrimaryAction(),
      chartContextMenuEnabled: this.chartContextMenuEnabled(),
      channelHighlightEnabled: this.channelHighlightEnabled(),
      dimOnHover: this.dimOnHover(),
      hoverHighlightColor: this.hoverHighlightColor(),
      highlightGatePieces: this.highlightGatePieces(),
      dimOneSideOnly: this.dimOneSideOnly(),
      basicPanelChartLink: this.basicPanelChartLink(),
      communityContentEnabled: this.communityContentEnabled(),
      alternativeIChingEnabled: this.alternativeIChingEnabled(),
      raLineTextsEnabled: this.raLineTextsEnabled(),
      ownLineTextsEnabled: this.ownLineTextsEnabled(),
      classicIChingEnabled: this.classicIChingEnabled(),
      ichingCastMode: this.ichingCastMode(),
      ichingCastDistribution: this.ichingCastDistribution(),
      ichingIntroCollapsed: this.ichingIntroCollapsed(),
      classicIChingIconsChart: this.classicIChingIconsChart(),
      classicIChingIconsTransits: this.classicIChingIconsTransits(),
      gateInfoOpenBehavior: this.gateInfoOpenBehavior(),
      gateGraphPalette: this.gateGraphPalette(),
      gateGraphSpacing: this.gateGraphSpacing(),
      docLinkOpenBehavior: this.docLinkOpenBehavior(),
      gateInfoFromChannelSeparateToDock: this.gateInfoFromChannelSeparateToDock(),
      advancedBridgeCalculation: this.advancedBridgeCalculation(),
      bridgeGateStyle: this.bridgeGateStyle(),
      moonTransitConnectorStyle: this.moonTransitConnectorStyle(),
      moonTransitGateMarkerStyle: this.moonTransitGateMarkerStyle(),
      justNowTransitColorMode: this.justNowTransitColorMode(),
      locksKeysPeriod: this.locksKeys.periodId(),
      locksKeysChartOverlay: this.locksKeys.chartOverlayMode(),
      locksKeysSearchSort: this.locksKeys.searchSort(),
      locksKeysSearchFilter: this.locksKeys.searchFilter(),
      mandalaQuarterColorVariant: this.mandalaQuarterColorVariant(),
      mandalaZodiacRingVariant: this.mandalaZodiacRingVariant(),
      mandalaSliceCenterFade: this.mandalaSliceCenterFade(),
      configPanelCollapsed: this.configPanelCollapsed(),
      configPanelWidth: this.configPanelWidth(),
      contentLangOverride: this.contentLang.langOverride(),
      settingsPanelOpen: this.panelOpen(),
      shortcutButtonSize: this.shortcutButtonSize(),
      chartMatchScope: this.chartMatchScope(),
      gateSearchSort: this.gateSearchSort(),
      // Velden die hun eigen component beheert (transits-panelen,
      // levenstijdlijn-pagina): overnemen zodat een full persist ze niet
      // terugzet op de default.
      ...pickExternallyManagedSettings(),
    };
  }

  /** Onmiddellijk wegschrijven (annuleert de debounce). */
  flush(): void {
    if (this.persistTimer != null) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    saveHdChartGlobalSettings(this.collect(), this.profile);
  }

  private schedulePersist(settings: HdChartGlobalSettingsV1): void {
    if (this.persistTimer != null) {
      clearTimeout(this.persistTimer);
    }
    const profile = this.profile;
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      saveHdChartGlobalSettings(settings, profile);
    }, PERSIST_DEBOUNCE_MS);
  }

  // ── Handlers (verplaatst uit de chart-basis/subklasse; pure signal-mutaties) ──

  handleColorConfigChange(colorConfig: HdBodygraphColorConfig): void {
    this.colorConfig.set(colorConfig);
  }

  handlePlanetGridColorConfigChange(config: HdPlanetGridColorConfig): void {
    this.planetGridColorConfig.set(config);
  }

  handleColorConfigPresetChange(presetId: HdBodygraphColorConfigPresetId): void {
    this.colorConfigPresetId.set(presetId);
    const preset = HD_BODYGRAPH_COLOR_CONFIG_PRESETS.find(entry => entry.id === presetId);
    if (preset) this.colorConfig.set(preset.config);
  }

  handlePlanetGridColorConfigPresetChange(presetId: HdPlanetGridColorConfigPresetId): void {
    this.planetGridColorConfigPresetId.set(presetId);
    const preset = HD_PLANET_GRID_COLOR_CONFIG_PRESETS.find(entry => entry.id === presetId);
    if (preset) this.planetGridColorConfig.set(preset.config);
  }

  handleCenterBorderStyleChange(style: HdBodygraphCenterBorderStyle): void {
    this.colorConfig.update(config => ({ ...config, centerBorderStyle: style }));
  }

  handleActiveGateColorPresetChange(preset: HdBodygraphActiveGateColorPreset): void {
    this.colorConfig.update(config => ({ ...config, activeGateColorPreset: preset }));
  }

  handleUndefinedStyleChange(style: HdUndefinedActivationStyle): void {
    this.planetGridColorConfig.update(config => ({ ...config, undefinedStyle: style }));
  }

  handleNodeBackgroundStyleChange(style: HdNodeBackgroundStyle): void {
    this.planetGridColorConfig.update(config => ({ ...config, nodeBackgroundStyle: style }));
  }

  handleBeneathTheLineStyleChange(style: HdBeneathTheLineStyle): void {
    this.planetGridColorConfig.update(config => ({ ...config, beneathTheLineStyle: style }));
  }

  handleGateNumberAlignChange(align: HdGateNumberAlign): void {
    this.planetGridColorConfig.update(config => ({ ...config, gateNumberAlign: align }));
  }

  handleGateNumberFontSizeChange(size: HdGateNumberFontSize): void {
    this.planetGridColorConfig.update(config => ({ ...config, gateNumberFontSize: size }));
  }

  handleBaseRotationChange(rotation: HdBaseRotation): void {
    this.planetGridColorConfig.update(config => ({ ...config, baseRotation: rotation }));
  }

  handlePrimaryGateActionChange(action: HdGatePrimaryAction): void {
    this.chartGatePrimaryAction.set(action);
  }

  handleAdvancedBridgeCalculationChange(enabled: boolean): void {
    this.advancedBridgeCalculation.set(enabled);
  }

  handleBridgeGateStyleChange(style: HdBridgeGateStyle): void {
    this.bridgeGateStyle.set(style);
  }

  handleContextMenuEnabledToggled(): void {
    this.chartContextMenuEnabled.update(value => !value);
  }

  handleChannelHighlightToggled(): void {
    this.channelHighlightEnabled.update(value => !value);
  }

  toggleDimOnHover(): void {
    this.dimOnHover.update(value => !value);
  }

  toggleBasicPanelChartLink(): void {
    this.basicPanelChartLink.update(value => !value);
  }

  handleHoverHighlightColorChange(color: HdHoverHighlightColor): void {
    this.hoverHighlightColor.set(coerceHdHoverHighlightColor(color));
  }

  toggleHighlightGatePieces(): void {
    this.highlightGatePieces.update(value => !value);
  }

  toggleDimOneSideOnly(): void {
    this.dimOneSideOnly.update(value => !value);
  }

  toggleCommunityContentEnabled(): void {
    this.communityContentEnabled.update(value => !value);
  }

  toggleAlternativeIChing(): void {
    this.alternativeIChingEnabled.update(value => !value);
  }

  toggleRaLineTexts(): void {
    this.raLineTextsEnabled.update(value => !value);
  }

  toggleOwnLineTexts(): void {
    this.ownLineTextsEnabled.update(value => !value);
  }

  toggleClassicIChing(): void {
    this.classicIChingEnabled.update(value => !value);
  }

  setIChingCastMode(mode: IChingCastMode): void {
    this.ichingCastMode.set(mode);
  }

  setIChingCastDistribution(distribution: IChingCastDistribution): void {
    this.ichingCastDistribution.set(distribution);
  }

  setIChingIntroCollapsed(collapsed: boolean): void {
    this.ichingIntroCollapsed.set(collapsed);
  }

  toggleClassicIChingIconsChart(): void {
    this.classicIChingIconsChart.update(value => !value);
  }

  toggleClassicIChingIconsTransits(): void {
    this.classicIChingIconsTransits.update(value => !value);
  }

  handleGateInfoFromChannelSeparateToDockToggled(): void {
    this.gateInfoFromChannelSeparateToDock.update(value => !value);
  }

  handleGateInfoOpenBehaviorChange(behavior: HdGateInfoOpenBehavior): void {
    this.gateInfoOpenBehavior.set(behavior);
  }

  handleDocLinkOpenBehaviorChange(behavior: HdDocLinkOpenBehavior): void {
    this.docLinkOpenBehavior.set(behavior);
  }

  handleGateGraphPaletteChange(palette: HdGateGraphPaletteId): void {
    this.gateGraphPalette.set(palette);
  }

  togglePlanetGrid(): void {
    this.showPlanetGrid.update(visible => !visible);
  }

  toggleSimplePlanetGrid(): void {
    this.simplePlanetGrid.update(simple => !simple);
  }

  toggleAdvancedLpsGrid(): void {
    this.advancedLpsGrid.update(advanced => !advanced);
  }

  toggleVisualizationMode(): void {
    const order: readonly HdPlanetGridVisualizationMode[] = ['classic', 'extended', 'modern', 'keynotes'];
    this.planetGridVisualizationMode.update(mode => order[(order.indexOf(mode) + 1) % order.length]);
  }

  setVisualizationMode(mode: string): void {
    const modes: readonly HdPlanetGridVisualizationMode[] = ['classic', 'extended', 'modern', 'keynotes'];
    if ((modes as readonly string[]).includes(mode)) {
      this.planetGridVisualizationMode.set(mode as HdPlanetGridVisualizationMode);
    }
  }

  toggleGeneKeys(): void {
    const next = !this.showGeneKeys();
    this.showGeneKeys.set(next);
    if (next) this.showHdKeynotes.set(false);
  }

  toggleHdKeynotes(): void {
    const next = !this.showHdKeynotes();
    this.showHdKeynotes.set(next);
    if (next) this.showGeneKeys.set(false);
  }

  toggleKeynotesAtHoverOnly(): void {
    this.keynotesAtHoverOnly.update(value => !value);
  }

  toggleCenterCheatSheet(): void {
    this.showCenterCheatSheet.update(value => !value);
  }

  toggleBothCenterStateSections(): void {
    this.showBothCenterStateSections.update(value => !value);
  }

  toggleShowGateBlocks(): void {
    this.showGateBlocks.set(!this.showGateBlocks());
  }

  toggleIncludeNonHdFeatures(): void {
    this.includeNonHdFeatures.update(include => !include);
    if (!this.includeNonHdFeatures()) {
      this.showGeneKeys.set(false);
    }
  }

  /** Dispatch van de data-driven stijl-rijen van het settings-paneel. */
  handleVisualizationStyleChange(change: { key: string; optionId: string }): void {
    switch (change.key) {
      case 'centerBorderStyle':
        this.handleCenterBorderStyleChange(change.optionId as HdBodygraphCenterBorderStyle);
        break;
      case 'activeGateColorPreset':
        this.handleActiveGateColorPresetChange(change.optionId as HdBodygraphActiveGateColorPreset);
        break;
      case 'undefinedStyle':
        this.handleUndefinedStyleChange(change.optionId as HdUndefinedActivationStyle);
        break;
      case 'nodeBackgroundStyle':
        this.handleNodeBackgroundStyleChange(change.optionId as HdNodeBackgroundStyle);
        break;
      case 'beneathTheLineStyle':
        this.handleBeneathTheLineStyleChange(change.optionId as HdBeneathTheLineStyle);
        break;
      case 'gateNumberAlign':
        this.handleGateNumberAlignChange(change.optionId as HdGateNumberAlign);
        break;
      case 'gateNumberFontSize':
        this.handleGateNumberFontSizeChange(change.optionId as HdGateNumberFontSize);
        break;
      case 'baseRotation':
        this.handleBaseRotationChange(change.optionId as HdBaseRotation);
        break;
      case 'advancedBridgeCalculation':
        this.handleAdvancedBridgeCalculationChange(change.optionId === 'on');
        break;
      case 'bridgeGateStyle':
        this.handleBridgeGateStyleChange(change.optionId as HdBridgeGateStyle);
        break;
      case 'planetGridVisualizationMode':
        this.setVisualizationMode(change.optionId);
        break;
      case 'showChiron':
        this.showChiron.set(change.optionId === 'on');
        break;
      case 'showAstroPositions':
        this.showAstroPositions.set(change.optionId === 'on');
        break;
      case 'moonTransitConnectorStyle':
        this.moonTransitConnectorStyle.set(change.optionId as HdMoonTransitConnectorStyle);
        break;
      case 'moonTransitGateMarkerStyle':
        this.moonTransitGateMarkerStyle.set(change.optionId as HdMoonTransitGateMarkerStyle);
        break;
      case 'justNowTransitColorMode':
        this.justNowTransitColorMode.set(change.optionId as HdJustNowTransitColorMode);
        break;
      case 'mandalaQuarterColorVariant':
        this.mandalaQuarterColorVariant.set(change.optionId as HdMandalaQuarterColorVariant);
        break;
      case 'mandalaZodiacRingVariant':
        this.mandalaZodiacRingVariant.set(change.optionId as HdMandalaZodiacRingVariant);
        break;
      case 'mandalaSliceCenterFade':
        this.mandalaSliceCenterFade.set(change.optionId === 'on');
        break;
    }
  }
}

/** Enkel een eindige, positieve breedte; al het andere (oude of corrupte blobs) = standaard. */
function validConfigPanelWidth(width: unknown): number | undefined {
  return typeof width === 'number' && Number.isFinite(width) && width > 0 ? Math.round(width) : undefined;
}
